import React, { type ComponentProps, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { AgentCard, type AgentCardProps } from '@atlassian/agent-profile-card/card';
import { IdentityUserAri } from '@atlassian/ari';

import type {
	UserProfileCardRelay_query$data,
	UserProfileCardRelay_query$key,
} from './__generated__/UserProfileCardRelay_query.graphql';
import { CardError } from './components/CardError';
import { CardPopupWrapper } from './components/CardPopupWrapper';
import { UserProfileCard } from './UserProfileCard';

type UserProfileCardPassThroughProps = Pick<
	ComponentProps<typeof UserProfileCard>,
	'cloudId' | 'testId' | 'isLoading' | 'actions' | 'viewerId'
>;

type ActivityObjectData = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<UserProfileCardRelay_query$data['activity']>['workedOn']
		>['edges'][number]
	>['node']
>['object']['data'];

/**
 * Derives a canonical URL for a recent-activity object, covering every
 * URL-bearing activity type returned by the full profile-page query.
 * Returns a single-element array (for `flatMap`) or an empty array when the
 * object type has no resolvable URL.
 */
function extractActivityUrl(objectData: ActivityObjectData | null | undefined): string[] {
	if (!objectData) {
		return [];
	}
	switch (objectData.__typename) {
		case 'ConfluencePage':
		case 'ConfluenceBlogPost':
		case 'ConfluenceWhiteboard':
		case 'ConfluenceDatabase':
		case 'ConfluenceEmbed':
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment':
			return objectData.links?.base && objectData.links?.webUi
				? [`${objectData.links.base}${objectData.links.webUi}`]
				: [];
		case 'JiraIssue':
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
		case 'DevOpsDocument':
		case 'DevOpsDesign':
			return objectData.webUrl ? [objectData.webUrl] : [];
		case 'TrelloBoard':
		case 'TrelloCard':
			return objectData.trelloUrl ? [objectData.trelloUrl] : [];
		case 'TownsquareGoal':
		case 'TownsquareProject':
		case 'TownsquareComment':
		case 'ExternalDocument':
		case 'DevOpsPullRequestDetails':
		case 'ExternalPullRequest':
		case 'ExternalRemoteLink':
		case 'MercuryFocusArea':
		case 'MercuryPortfolio':
		case 'RadarPosition':
		case 'RadarView':
		case 'CompassComponent':
			return objectData.url ? [objectData.url] : [];
		case 'BitbucketPullRequest':
			return objectData.href ? [objectData.href] : [];
		case 'LoomVideo':
			return objectData.loomUrl ? [objectData.loomUrl] : [];
		case 'AssetsObject':
		case 'AssetsObjectType':
		case 'AssetsSchema':
			return objectData.links?.web ? [objectData.links.web] : [];
		default:
			return [];
	}
}

/**
 * Computes the de-duplication key for a recent-activity object, mirroring the
 * full profile page's `deduplicateId` behaviour:
 * - Confluence comments de-duplicate on their container (page/blog/whiteboard),
 *   so multiple comments on the same page collapse to a single entry
 *   (equivalent to `deduplicateId: ['container.id']`).
 * - Jira comments de-duplicate on their parent issue
 *   (equivalent to `deduplicateId: ['issue.id']`).
 * - Everything else de-duplicates on its own URL
 *   (equivalent to `deduplicateId: ['objectId']`).
 *
 * Falls back to the object's own URL when the container/issue is unavailable.
 */
function getActivityDedupeKey(objectData: ActivityObjectData | null | undefined): string {
	const ownUrl = extractActivityUrl(objectData)[0] ?? '';
	if (!objectData) {
		return ownUrl;
	}
	switch (objectData.__typename) {
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment': {
			const container = objectData.container;
			const containerUrl =
				container && 'links' in container && container.links?.base && container.links?.webUi
					? `${container.links.base}${container.links.webUi}`
					: '';
			return containerUrl || ownUrl;
		}
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
			return objectData.issue?.webUrl || ownUrl;
		default:
			return ownUrl;
	}
}

/** Agent-card config forwarded to `AgentCard`. `accountId` / `cloudId` come from the Relay data. */
export type UserProfileCardRelayAgentProps = Omit<AgentCardProps, 'accountId' | 'cloudId'>;

interface UserProfileCardRelayProps extends UserProfileCardPassThroughProps {
	/** Relay fragment ref returned by the query that spreads `...UserProfileCardRelay_query`. */
	queryFragment: UserProfileCardRelay_query$key;
	/** Agent-card config. Required for agent subjects. Omitting renders `CardError` if the subject is an agent. */
	agent?: UserProfileCardRelayAgentProps;
}

/**
 * Renders a user profile card from a Relay fragment.
 *
 * The fragment is colocated: it contains the user's base profile (name, avatar,
 * email, extendedProfile) and the supplemental data (manager, third-party
 * integrations, recent work) in one spread, so everything fetches in a single
 * round-trip.
 *
 * Recent-work previews need a `SmartCardProvider` from `@atlaskit/link-provider`
 * in scope. Atlassian product shells provide this by default.
 *
 * @example
 * ```tsx
 * const data = useLazyLoadQuery<MyQuery>(
 *   graphql`
 *     query MyQuery($userId: ID!, $cloudId: String!, $orgId: String!, $viewerId: String!) {
 *       ...UserProfileCardRelay_query @arguments(
 *         userId: $userId, cloudId: $cloudId, orgId: $orgId, viewerId: $viewerId
 *       )
 *     }
 *   `,
 *   buildQueryVariables({ userId, cloudId, orgId, viewerId }),
 * );
 * return <UserProfileCardRelay queryFragment={data} cloudId={cloudId} />;
 * ```
 */
function UserProfileCardRelay({
	queryFragment,
	cloudId,
	testId,
	isLoading,
	agent,
	actions,
	viewerId,
}: UserProfileCardRelayProps): React.JSX.Element {
	const data = useFragment<UserProfileCardRelay_query$key>(
		graphql`
			fragment UserProfileCardRelay_query on Query
			@argumentDefinitions(
				userId: { type: "ID!" }
				userIdString: { type: "String!" }
				cloudId: { type: "String!" }
				orgId: { type: "String!" }
				viewerId: { type: "String!" }
				userAri: { type: "ID!" }
				skipKnowledgeDiscovery: { type: "Boolean!", defaultValue: false }
			) {
				user(accountId: $userId) {
					accountId
					name
					picture
					... on AtlassianAccountUser {
						email
						extendedProfile {
							jobTitle
							department
							location
							organization
						}
					}
					... on AppUser {
						appType
					}
				}
				activity {
					workedOn(first: 50, filter: { actors: [$userAri] }) {
						edges {
							node {
								object {
									data {
										__typename
										... on ConfluencePage {
											links {
												base
												webUi
											}
										}
										... on ConfluenceBlogPost {
											links {
												base
												webUi
											}
										}
										... on ConfluenceWhiteboard {
											links {
												base
												webUi
											}
										}
										... on ConfluenceDatabase {
											links {
												base
												webUi
											}
										}
										... on ConfluenceEmbed {
											links {
												base
												webUi
											}
										}
										... on ConfluenceFooterComment {
											links {
												base
												webUi
											}
											container {
												__typename
												... on ConfluencePage {
													links {
														base
														webUi
													}
												}
												... on ConfluenceBlogPost {
													links {
														base
														webUi
													}
												}
												... on ConfluenceWhiteboard {
													links {
														base
														webUi
													}
												}
											}
										}
										... on ConfluenceInlineComment {
											links {
												base
												webUi
											}
											container {
												__typename
												... on ConfluencePage {
													links {
														base
														webUi
													}
												}
												... on ConfluenceBlogPost {
													links {
														base
														webUi
													}
												}
												... on ConfluenceWhiteboard {
													links {
														base
														webUi
													}
												}
											}
										}
										... on TownsquareGoal {
											url
										}
										... on TownsquareProject {
											url
										}
										... on TownsquareComment {
											url
										}
										... on JiraIssue {
											webUrl
										}
										... on JiraPlatformComment {
											webUrl
											issue {
												webUrl
											}
										}
										... on JiraServiceManagementComment {
											webUrl
											issue {
												webUrl
											}
										}
										... on TrelloBoard {
											trelloUrl: url
										}
										... on TrelloCard {
											trelloUrl: url
										}
										... on DevOpsDocument {
											webUrl: url
										}
										... on ExternalDocument {
											url
										}
										... on DevOpsPullRequestDetails {
											url
										}
										... on ExternalPullRequest {
											url
										}
										... on DevOpsDesign {
											webUrl: url
										}
										... on ExternalRemoteLink {
											url
										}
										... on MercuryFocusArea {
											url
										}
										... on MercuryPortfolio {
											url
										}
										... on RadarPosition {
											url
										}
										... on RadarView {
											url
										}
										... on CompassComponent {
											url
										}
										... on BitbucketPullRequest {
											href
										}
										... on LoomVideo {
											loomUrl: url
										}
										... on AssetsObject {
											links {
												web
											}
										}
										... on AssetsObjectType {
											links {
												web
											}
										}
										... on AssetsSchema {
											links {
												web
											}
										}
									}
								}
							}
						}
					}
				}
				knowledgeDiscovery @skip(if: $skipKnowledgeDiscovery) {
					singleUser(
						orgId: $orgId
						tenantId: $cloudId
						accountId: $userIdString
						requestingUserId: $viewerId
					) @optIn(to: "KnowledgeDiscovery Single User") {
						__typename
						... on KnowledgeDiscoverySingleUser {
							user {
								manager {
									id
									jobTitle
									picture
									name
								}
								thirdPartyIntegrations {
									externalId
									integrationId
									product
								}
							}
						}
					}
				}
			}
		`,
		queryFragment,
	);

	return (
		<UserProfileCardRelayContent
			data={data}
			cloudId={cloudId}
			testId={testId}
			isLoading={isLoading}
			agent={agent}
			actions={actions}
			viewerId={viewerId}
		/>
	);
}

function UserProfileCardRelayContent({
	data,
	cloudId,
	testId,
	isLoading,
	agent,
	actions,
	viewerId,
}: {
	data: UserProfileCardRelay_query$data;
	cloudId: string;
	testId?: string;
	isLoading?: boolean;
	agent?: UserProfileCardRelayAgentProps;
} & Pick<UserProfileCardPassThroughProps, 'actions' | 'viewerId'>): React.JSX.Element {
	const user = data.user;
	const accountId = user?.accountId ?? '';

	const rawSingleUser = data.knowledgeDiscovery?.singleUser;
	const knowledgeSingleUser = rawSingleUser && 'user' in rawSingleUser ? rawSingleUser : null;
	const rawManager = knowledgeSingleUser?.user?.manager;

	const recentWorkData = useMemo(() => {
		const edges = data.activity?.workedOn?.edges ?? [];
		// De-duplicate by the profile page's `deduplicateId` key (comments collapse
		// onto their container/issue), keeping the first occurrence, then resolve
		// each survivor to its display URL and cap to the first 3 links.
		const seenKeys = new Set<string>();
		const urls: string[] = [];
		for (const edge of edges) {
			const objectData = edge?.node?.object?.data;
			const displayUrl = extractActivityUrl(objectData)[0];
			if (!displayUrl) {
				continue;
			}
			const dedupeKey = getActivityDedupeKey(objectData);
			if (seenKeys.has(dedupeKey)) {
				continue;
			}
			seenKeys.add(dedupeKey);
			urls.push(displayUrl);
			if (urls.length === 3) {
				break;
			}
		}
		return urls.length ? urls : undefined;
	}, [data.activity?.workedOn?.edges]);

	const appType = user && 'appType' in user ? user.appType : null;
	const isAgent = typeof appType === 'string' && appType.toLowerCase() === 'agent';

	const profileData = useMemo(() => {
		if (!user || isAgent) {
			return null;
		}
		const knowledge = knowledgeSingleUser?.user;

		const manager = rawManager?.id
			? {
					id: rawManager.id,
					name: rawManager.name,
					avatarUrl: rawManager.picture,
					jobTitle: rawManager.jobTitle,
				}
			: null;

		const thirdPartyIntegrations = knowledge?.thirdPartyIntegrations
			?.filter(
				(
					integration,
				): integration is { externalId: string; integrationId: string; product: string } =>
					Boolean(integration?.externalId && integration?.integrationId && integration?.product),
			)
			.map((integration) => ({
				externalId: integration.externalId,
				integrationId: integration.integrationId,
				product: integration.product,
			}));

		return {
			accountId: user.accountId,
			name: user.name ?? '',
			avatarUrl: user.picture,
			email: user.email,
			jobTitle: user.extendedProfile?.jobTitle,
			department: user.extendedProfile?.department,
			location: user.extendedProfile?.location,
			organization: user.extendedProfile?.organization,
			manager,
			thirdPartyIntegrations: thirdPartyIntegrations?.length ? thirdPartyIntegrations : null,
		};
	}, [user, isAgent, knowledgeSingleUser, rawManager]);

	if (!accountId) {
		return <CardError testId={testId} />;
	}

	if (isAgent) {
		if (!agent) {
			return <CardError testId={testId} />;
		}
		return (
			<CardPopupWrapper>
				<AgentCard {...agent} accountId={accountId} cloudId={cloudId} />
			</CardPopupWrapper>
		);
	}

	if (!profileData) {
		return <CardError testId={testId} />;
	}

	return (
		<CardPopupWrapper>
			<UserProfileCard
				cloudId={cloudId}
				testId={testId}
				isLoading={isLoading}
				profileData={profileData}
				recentWorkData={recentWorkData}
				actions={actions}
				viewerId={viewerId}
			/>
		</CardPopupWrapper>
	);
}

type UserProfileCardQueryParams = {
	/** Account ID of the user whose card to render. */
	userId: string;
	/** Site / tenant cloud ID. Omit to skip the knowledge-discovery section. */
	cloudId?: string;
	/** Organisation ID. Omit to skip the knowledge-discovery section. */
	orgId?: string;
	/** Account ID of the currently logged-in user (the viewer). Omit to skip the knowledge-discovery section. */
	viewerId?: string;
};

/**
 * Builds the variables for a query that spreads `UserProfileCardRelay_query`.
 *
 * The fragment needs a few extra variables (`userIdString`, `userAri`) that are just
 * derived from `userId` to satisfy the GraphQL schema's scalar types. This helper
 * handles that plumbing so consumers only think about the four semantic inputs.
 *
 * @example
 * ```tsx
 * const data = useLazyLoadQuery<MyQuery>(
 *   MY_QUERY,
 *   buildQueryVariables({ userId, cloudId, orgId, viewerId }),
 * );
 * ```
 */
type UserProfileCardQueryVariables = {
	userId: string;
	userIdString: string;
	cloudId: string;
	orgId: string;
	viewerId: string;
	userAri: string;
	skipKnowledgeDiscovery: boolean;
};

function buildQueryVariables({
	userId,
	cloudId,
	orgId,
	viewerId,
}: UserProfileCardQueryParams): UserProfileCardQueryVariables {
	const skipKnowledgeDiscovery = !cloudId || !orgId || !viewerId;

	return {
		userId,
		userIdString: userId,
		cloudId: cloudId ?? '',
		orgId: orgId ?? '',
		viewerId: viewerId ?? '',
		userAri: IdentityUserAri.create({ userId }).toString(),
		skipKnowledgeDiscovery,
	};
}

export { UserProfileCardRelay, buildQueryVariables };
export type { UserProfileCardRelayProps, UserProfileCardQueryParams };
